import { createMintInstruction } from '@metaplex-foundation/mpl-token-metadata';
import { SYSVAR_INSTRUCTIONS_PUBKEY } from '@solana/web3.js';
import { parseTokenMetadataAuthorization } from '../Authorization.mjs';
import { useOperation } from '../../../types/Operation.mjs';
import { token } from '../../../types/Amount.mjs';
import { TransactionBuilder } from '../../../utils/TransactionBuilder.mjs';
import { isNonFungible } from '../models/Metadata.mjs';

// -----------------
// Operation
// -----------------

const Key = 'MintNftOperation';

/**
 * Mint token(s) for an NFT or SFT.
 *
 * ```ts
 * await metaplex.nfts().mint({
 *   nftOrSft,
 *   toOwner,
 *   amount: token(5),
 * });
 * ```
 *
 * @group Operations
 * @category Constructors
 */
const mintNftOperation = useOperation(Key);

/**
 * @group Operations
 * @category Types
 */

/**
 * @group Operations
 * @category Handlers
 */
const mintNftOperationHandler = {
  handle: async (operation, metaplex, scope) => {
    return mintNftBuilder(metaplex, operation.input, scope).sendAndConfirm(metaplex, scope.confirmOptions);
  }
};

// -----------------
// Builder
// -----------------

/**
 * @group Transaction Builders
 * @category Inputs
 */

/**
 * Mint token(s) for an NFT or SFT.
 *
 * ```ts
 * const transactionBuilder = metaplex
 *   .nfts()
 *   .builders()
 *   .mint({
 *     nftOrSft,
 *     toOwner,
 *     amount: token(5),
 *   });
 * ```
 *
 * @group Transaction Builders
 * @category Constructors
 */
const mintNftBuilder = (metaplex, params, options = {}) => {
  const {
    programs,
    payer = metaplex.rpc().getDefaultFeePayer()
  } = options;
  const {
    nftOrSft,
    authority = metaplex.identity(),
    authorizationDetails,
    toOwner = metaplex.identity().publicKey,
    amount = token(1)
  } = params;

  // Auth.
  const auth = parseTokenMetadataAuthorization(metaplex, {
    mint: nftOrSft.address,
    authority: '__kind' in authority ? authority : {
      __kind: 'metadata',
      updateAuthority: authority
    },
    authorizationDetails,
    programs
  });

  // Programs.
  const tokenMetadataProgram = metaplex.programs().getTokenMetadata(programs);
  const ataProgram = metaplex.programs().getAssociatedToken(programs);
  const tokenProgram = metaplex.programs().getToken(programs);
  const systemProgram = metaplex.programs().getSystem(programs);

  // PDAs.
  const metadata = metaplex.nfts().pdas().metadata({
    mint: nftOrSft.address,
    programs
  });
  const masterEdition = metaplex.nfts().pdas().masterEdition({
    mint: nftOrSft.address,
    programs
  });

  // Destination token account.
  const toToken = params.toToken ?? metaplex.tokens().pdas().associatedTokenAccount({
    mint: nftOrSft.address,
    owner: toOwner,
    programs
  });
  return TransactionBuilder.make().setFeePayer(payer)

  // Update the metadata account.
  .add({
    instruction: createMintInstruction({
      token: toToken,
      tokenOwner: toOwner,
      metadata,
      masterEdition: isNonFungible(nftOrSft) ? masterEdition : undefined,
      tokenRecord: metaplex.nfts().pdas().tokenRecord({
        mint: nftOrSft.address,
        token: toToken,
        programs
      }),
      mint: nftOrSft.address,
      authority: auth.accounts.authority,
      // delegateRecord, // TODO: add when program supports Authority delegate.
      payer: payer.publicKey,
      systemProgram: systemProgram.address,
      sysvarInstructions: SYSVAR_INSTRUCTIONS_PUBKEY,
      splTokenProgram: tokenProgram.address,
      splAtaProgram: ataProgram.address,
      authorizationRules: auth.accounts.authorizationRules
      // authorizationRulesProgram,
    }, {
      mintArgs: {
        __kind: 'V1',
        amount: amount.basisPoints,
        ...auth.data
      }
    }, tokenMetadataProgram.address),
    signers: [payer, ...auth.signers],
    key: params.instructionKey ?? 'mintNft'
  });
};

export { mintNftBuilder, mintNftOperation, mintNftOperationHandler };
//# sourceMappingURL=mintNft.mjs.map
